import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react'
import { useTimeline } from '../../context/useTimeline'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { AuthContext } from '../../context/authContextCore'
import { subscribeWorkspaceUserDirectory, type WorkspaceUserDirectoryEntry } from '../../services/workspaceUserDirectory'
import {
  getOverviewSummary,
  subscribeOverviewV2,
  type OverviewSummaryResult,
  type OverviewSummaryTier,
} from '../../services/overviewService'
import {
  getReliabilityLeaderboard,
  subscribeReliabilityStats,
  type ReliabilityLeaderboardRow,
  type ReliabilitySnapshot,
} from '../../services/reliabilityService'
import { subscribeWorkspaceBudgetSummary } from '../../services/budgetService'
import type { WorkspaceBudgetSummary } from '../../types/budget'
import { subscribeWorkspaceStock } from '../../services/stockService'
import type { StockItem } from '../../types/stock'
import { isModuleEnabled } from '../../utils/workspaceModules'
import {
  personStats,
  teamStats,
  goalProgress,
  upcomingDates,
  healthOf,
  viewerTier,
  trimForTier,
  attentionItems,
  type OverviewTierData,
  type HealthResult,
  type ViewerTier,
} from '../../utils/overviewInsights'
import type { OverviewDocumentV2 } from '../../types/overview'
import type { Workspace } from '../../types/workspace'

export interface UseOverviewDataOptions {
  workspaceId?: string
  workspace?: Workspace | null
  uid?: string | null
  role?: string | null
  isOwner?: boolean
}

export interface UseOverviewDataResult extends OverviewTierData {
  tier: ViewerTier
  health: HealthResult
  overviewDoc: OverviewDocumentV2 | null
  summary: OverviewSummaryResult | null
  reliabilityLeaderboard?: ReliabilityLeaderboardRow[] | null
  reliabilityStats?: ReliabilitySnapshot | null
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

export const useOverviewData = (options: UseOverviewDataOptions = {}): UseOverviewDataResult => {
  const { elements, checklistItems } = useTimeline()
  const workspaceCtx = useContext(WorkspaceContext)
  const authCtx = useContext(AuthContext)

  const effectiveWorkspaceId =
    options.workspaceId ??
    options.workspace?.id ??
    workspaceCtx?.activeWorkspaceId ??
    ''

  const effectiveWorkspace =
    options.workspace ??
    workspaceCtx?.workspaces?.find((w) => w.id === effectiveWorkspaceId) ??
    null

  const currentUser = authCtx?.currentUser
  const effectiveUid = options.uid ?? currentUser?.uid ?? ''
  const effectiveRole =
    options.role ??
    (effectiveWorkspaceId && workspaceCtx?.getWorkspaceRole
      ? workspaceCtx.getWorkspaceRole(effectiveWorkspaceId)
      : workspaceCtx?.activeRole) ??
    'VIEWER'

  const isOwner =
    options.isOwner ??
    Boolean(effectiveWorkspace && effectiveUid && effectiveWorkspace.ownerId === effectiveUid)

  // Local state for all subscriptions
  const [directory, setDirectory] = useState<WorkspaceUserDirectoryEntry[]>([])
  const [overviewDoc, setOverviewDoc] = useState<OverviewDocumentV2 | null>(null)
  const [summary, setSummary] = useState<OverviewSummaryResult | null>(null)
  const [leaderboard, setLeaderboard] = useState<ReliabilityLeaderboardRow[] | null>(null)
  const [reliabilityStats, setReliabilityStats] = useState<ReliabilitySnapshot | null>(null)
  const [budgetSummary, setBudgetSummary] = useState<WorkspaceBudgetSummary | null>(null)
  const [stockItems, setStockItems] = useState<StockItem[]>([])
  const [loading, setLoading] = useState<boolean>(Boolean(effectiveWorkspaceId))
  const [error, setError] = useState<Error | null>(null)

  const activeWsRef = useRef(effectiveWorkspaceId)
  const workspaceRef = useRef(effectiveWorkspace)

  useEffect(() => {
    activeWsRef.current = effectiveWorkspaceId
  }, [effectiveWorkspaceId])

  useEffect(() => {
    workspaceRef.current = effectiveWorkspace
  }, [effectiveWorkspace])

  // Reset summary state on workspace switch during render
  const [prevWorkspaceId, setPrevWorkspaceId] = useState(effectiveWorkspaceId)
  if (prevWorkspaceId !== effectiveWorkspaceId) {
    setPrevWorkspaceId(effectiveWorkspaceId)
    setSummary(null)
    setError(null)
    setLoading(Boolean(effectiveWorkspaceId))
  }

  // Effective tier: computed from role or overridden by summary.tier when available
  const computedTier = viewerTier(effectiveRole, isOwner)
  const tier: ViewerTier = (summary?.tier as OverviewSummaryTier) ?? computedTier

  // Poll overview summary every 5 minutes and stop on unmount
  useEffect(() => {
    if (!effectiveWorkspaceId) {
      return undefined
    }

    let isCurrent = true

    const runFetch = () => {
      getOverviewSummary(effectiveWorkspaceId)
        .then((res) => {
          if (isCurrent && activeWsRef.current === effectiveWorkspaceId) {
            setSummary(res)
            setError(null)
            setLoading(false)
          }
        })
        .catch((err: unknown) => {
          if (isCurrent && activeWsRef.current === effectiveWorkspaceId) {
            setError(err instanceof Error ? err : new Error(String(err)))
            setLoading(false)
          }
        })
    }

    runFetch()

    const intervalId = setInterval(runFetch, 5 * 60 * 1000)

    return () => {
      isCurrent = false
      clearInterval(intervalId)
    }
  }, [effectiveWorkspaceId])

  // Subscribe directory
  useEffect(() => {
    if (!effectiveWorkspaceId) {
      return undefined
    }
    const unsub = subscribeWorkspaceUserDirectory(
      effectiveWorkspaceId,
      (entries) => {
        if (activeWsRef.current === effectiveWorkspaceId) {
          setDirectory(entries)
        }
      },
      () => setDirectory([]),
    )
    return () => {
      unsub?.()
      setDirectory([])
    }
  }, [effectiveWorkspaceId])

  // Subscribe overview v2 document
  useEffect(() => {
    if (!effectiveWorkspaceId) {
      return undefined
    }
    const unsub = subscribeOverviewV2(
      effectiveWorkspaceId,
      (doc) => {
        if (activeWsRef.current === effectiveWorkspaceId) {
          setOverviewDoc(doc)
        }
      },
      { workspace: workspaceRef.current },
    )
    return () => {
      unsub?.()
      setOverviewDoc(null)
    }
  }, [effectiveWorkspaceId])

  // Reliability subscription / query
  useEffect(() => {
    if (!effectiveWorkspaceId) {
      return undefined
    }
    if (tier === 'lead') {
      let isCurrent = true
      void getReliabilityLeaderboard(effectiveWorkspaceId)
        .then((res) => {
          if (isCurrent && activeWsRef.current === effectiveWorkspaceId) {
            setLeaderboard(res.rows)
          }
        })
        .catch(() => undefined)
      return () => {
        isCurrent = false
        setLeaderboard(null)
      }
    } else if (tier === 'helper' && effectiveUid) {
      const unsub = subscribeReliabilityStats(
        effectiveWorkspaceId,
        effectiveUid,
        (stats) => {
          if (activeWsRef.current === effectiveWorkspaceId) {
            setReliabilityStats(stats)
          }
        },
      )
      return () => {
        unsub?.()
        setReliabilityStats(null)
      }
    }
    return () => {
      setLeaderboard(null)
      setReliabilityStats(null)
    }
  }, [effectiveWorkspaceId, tier, effectiveUid])

  // Budget subscription (conditional on module enabled)
  const budgetEnabled = isModuleEnabled(effectiveWorkspace, 'budget')
  useEffect(() => {
    if (!effectiveWorkspaceId || !budgetEnabled) {
      return undefined
    }
    const unsub = subscribeWorkspaceBudgetSummary(
      effectiveWorkspaceId,
      (summaryData) => {
        if (activeWsRef.current === effectiveWorkspaceId) {
          setBudgetSummary(summaryData)
        }
      },
    )
    return () => {
      unsub?.()
      setBudgetSummary(null)
    }
  }, [effectiveWorkspaceId, budgetEnabled])

  // Stock subscription (conditional on module enabled)
  const stockEnabled = isModuleEnabled(effectiveWorkspace, 'stock')
  useEffect(() => {
    if (!effectiveWorkspaceId || !stockEnabled) {
      return undefined
    }
    const unsub = subscribeWorkspaceStock(
      effectiveWorkspaceId,
      (items) => {
        if (activeWsRef.current === effectiveWorkspaceId) {
          setStockItems(items)
        }
      },
    )
    return () => {
      unsub?.()
      setStockItems([])
    }
  }, [effectiveWorkspaceId, stockEnabled])

  // Compute insights
  const data = useMemo<OverviewTierData & { health: HealthResult }>(() => {
    const goals = elements.filter((el) => el.type === 'GOAL')
    const upcoming = upcomingDates(elements, 30)
    const tStats = teamStats(checklistItems, directory)
    const you = effectiveUid ? personStats(checklistItems, effectiveUid) : null
    const health = healthOf({ items: checklistItems, goals })
    const budgetPace = budgetSummary
      ? {
          usedMinor: budgetSummary.totalActualSpend,
          limitMinor: budgetSummary.totalBudgetLimit,
        }
      : null
    const attention = attentionItems({
      items: checklistItems,
      members: directory,
      goals,
      stockItems,
      budget: budgetPace,
      viewerUid: effectiveUid,
      viewerTier: tier,
    })

    const modules: Record<string, unknown> = {}
    if (budgetEnabled && budgetSummary) modules.budget = budgetSummary
    if (stockEnabled && stockItems.length > 0) modules.stock = stockItems
    if (tier === 'lead' && leaderboard) modules.reliability = leaderboard
    if (tier === 'helper' && reliabilityStats) modules.reliability = reliabilityStats

    return {
      you,
      teamStats: tStats,
      people: tStats.people,
      attention,
      goals: goalProgress(goals),
      upcoming,
      activityFeed: summary?.feed ?? null,
      modules: Object.keys(modules).length > 0 ? modules : null,
      health,
    }
  }, [
    elements,
    checklistItems,
    directory,
    effectiveUid,
    tier,
    stockItems,
    budgetSummary,
    budgetEnabled,
    stockEnabled,
    leaderboard,
    reliabilityStats,
    summary,
  ])

  // Role trimming by tier
  const trimmed = useMemo(() => {
    return trimForTier(data, tier, effectiveUid)
  }, [data, tier, effectiveUid])

  const refresh = useCallback(async () => {
    if (!effectiveWorkspaceId) return
    setLoading(true)
    try {
      const res = await getOverviewSummary(effectiveWorkspaceId)
      if (activeWsRef.current === effectiveWorkspaceId) {
        setSummary(res)
        setError(null)
      }
    } catch (err: unknown) {
      if (activeWsRef.current === effectiveWorkspaceId) {
        setError(err instanceof Error ? err : new Error(String(err)))
      }
    } finally {
      if (activeWsRef.current === effectiveWorkspaceId) {
        setLoading(false)
      }
    }
  }, [effectiveWorkspaceId])

  return {
    ...trimmed,
    tier,
    health: data.health,
    overviewDoc,
    summary,
    reliabilityLeaderboard: leaderboard,
    reliabilityStats,
    loading,
    error,
    refresh,
  }
}
